import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, type GitlabRole, type GitlabVisibility, type Project } from '../lib/api.ts';
import { Pagination } from '../components/Pagination.tsx';
import { RoleBadge } from '../components/RoleBadge.tsx';

type Tab = 'favorites' | 'imported' | 'available';

const PAGE_SIZE = 10;

const VISIBILITY_LABEL: Record<GitlabVisibility, string> = {
  private: 'Privé',
  internal: 'Interne',
  public: 'Public',
};

function VisibilityIcon({ visibility }: { visibility: GitlabVisibility | null }) {
  if (!visibility) return null;
  return (
    <svg
      className="repo-visibility-icon"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label={VISIBILITY_LABEL[visibility]}
    >
      <title>{VISIBILITY_LABEL[visibility]}</title>
      {visibility === 'private' && (
        <path
          d="M4.5 7V5a3.5 3.5 0 0 1 7 0v2M3.5 7h9a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z"
          stroke="currentColor"
          strokeWidth="1.3"
        />
      )}
      {visibility === 'internal' && (
        <path
          d="M8 1.5 2.5 3.7v3.6c0 3.4 2.3 6.2 5.5 7.2 3.2-1 5.5-3.8 5.5-7.2V3.7L8 1.5Z"
          stroke="currentColor"
          strokeWidth="1.3"
        />
      )}
      {visibility === 'public' && (
        <>
          <circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.3" />
          <path d="M1.8 8h12.4M8 1.8c1.6 1.7 2.5 4 2.5 6.2s-.9 4.5-2.5 6.2c-1.6-1.7-2.5-4-2.5-6.2s.9-4.5 2.5-6.2Z" stroke="currentColor" strokeWidth="1.3" />
        </>
      )}
    </svg>
  );
}

const REMOVABLE_ROLES: Set<GitlabRole> = new Set(['maintainer', 'owner']);

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      className="repo-star-icon"
      viewBox="0 0 16 16"
      fill={filled ? 'currentColor' : 'none'}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M8 1.6 9.9 5.6l4.4.6-3.2 3.1.8 4.4L8 11.6l-3.9 2.1.8-4.4-3.2-3.1 4.4-.6L8 1.6Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RemoveIcon() {
  return (
    <svg
      className="repo-star-icon"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.2" />
      <line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function AddIcon() {
  return (
    <svg
      className="repo-star-icon"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.2" />
      <line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="8" y1="5" x2="8" y2="11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function RepoTitle({
  name,
  pathWithNamespace,
  visibility,
  role,
}: {
  name: string;
  pathWithNamespace: string;
  visibility: GitlabVisibility | null;
  role: GitlabRole | null;
}) {
  const segments = pathWithNamespace.split('/');
  const groupPath = segments.slice(0, -1).join('/');
  return (
    <div className="repo-card-title">
      {groupPath && <span className="repo-group-path">{groupPath}/</span>}
      <strong>{name}</strong>
      <VisibilityIcon visibility={visibility} />
      <RoleBadge role={role} />
    </div>
  );
}

export function RepoListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('imported');
  const [favoritesPage, setFavoritesPage] = useState(1);
  const [importedPage, setImportedPage] = useState(1);
  const [availablePage, setAvailablePage] = useState(1);
  const [search, setSearch] = useState('');

  // Aligné sur PROJECTS_CACHE_TTL_MS côté API (streamAccessibleProjects) — pas la peine de
  // refetch plus souvent que le cache serveur ne se renouvelle.
  const available = useQuery({
    queryKey: ['available'],
    // Pousse chaque page reçue dans le cache React Query au fur et à mesure : `available.data`
    // se met à jour progressivement au lieu d'attendre les ~20s du scan GitLab complet.
    queryFn: () => api.streamAvailable((page) => qc.setQueryData(['available'], page)),
    staleTime: 60_000,
  });
  const imported = useQuery({ queryKey: ['repos'], queryFn: api.listRepos, staleTime: 60_000 });
  type ImportedRepo = NonNullable<typeof imported.data>[number];

  // Même clé de cache ['preferences'] que RepoViewPage.tsx/SettingsModal.tsx — un PATCH depuis
  // n'importe lequel de ces composants met à jour le cache partagé pour les autres.
  const preferencesQuery = useQuery({ queryKey: ['preferences'], queryFn: () => api.getPreferences() });
  const favoriteIds = new Set(preferencesQuery.data?.favoriteRepoIds ?? []);
  const favoriteMutation = useMutation({
    mutationFn: (favoriteRepoIds: string[]) => api.updatePreferences({ favoriteRepoIds }),
    onSuccess: (updated) => qc.setQueryData(['preferences'], updated),
  });
  const toggleFavorite = (repoId: string) => {
    const next = favoriteIds.has(repoId)
      ? [...favoriteIds].filter((id) => id !== repoId)
      : [...favoriteIds, repoId];
    favoriteMutation.mutate(next);
  };

  const [confirmingRemoveId, setConfirmingRemoveId] = useState<string | null>(null);
  const removeRepo = useMutation({
    mutationFn: (repoId: string) => api.removeRepo(repoId),
    onSuccess: async () => {
      setConfirmingRemoveId(null);
      await qc.invalidateQueries({ queryKey: ['repos'] });
    },
  });

  const importedIds = new Set((imported.data ?? []).map((r) => r.gitlabProjectId));
  const notImported = (available.data ?? []).filter((p) => !importedIds.has(p.id));

  const query = search.trim().toLowerCase();
  const matches = (name: string, pathWithNamespace: string) =>
    query === '' || name.toLowerCase().includes(query) || pathWithNamespace.toLowerCase().includes(query);
  // Alphabétique par nom plutôt que l'ordre renvoyé par l'API (importedAt/lastActivityAt décroissant,
  // voir routes/repos.ts et gitlab.ts) — celui-ci ne sert plus qu'à déterminer les ex-æquo, sans
  // conséquence visible une fois passé dans localeCompare.
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

  // Onglet à part (pas un tri favoris-en-tête ni une section dans "Dépôts importés") : un dépôt
  // favori reste visible dans les deux onglets — se marquer favori ne le retire pas de la liste
  // complète, ça donne juste un raccourci d'accès direct en plus.
  const importedFiltered = (imported.data ?? []).filter((r) => matches(r.name, r.pathWithNamespace)).sort(byName);
  const favoritesFiltered = importedFiltered.filter((r) => favoriteIds.has(r.id));
  const availableFiltered = notImported.filter((p) => matches(p.name, p.pathWithNamespace)).sort(byName);

  const favoritesTotalPages = Math.max(1, Math.ceil(favoritesFiltered.length / PAGE_SIZE));
  const favoritesCurrentPage = Math.min(favoritesPage, favoritesTotalPages);
  const favoritesRows = favoritesFiltered.slice(
    (favoritesCurrentPage - 1) * PAGE_SIZE,
    favoritesCurrentPage * PAGE_SIZE,
  );

  const importedTotalPages = Math.max(1, Math.ceil(importedFiltered.length / PAGE_SIZE));
  const importedCurrentPage = Math.min(importedPage, importedTotalPages);
  const importedRows = importedFiltered.slice(
    (importedCurrentPage - 1) * PAGE_SIZE,
    importedCurrentPage * PAGE_SIZE,
  );

  const availableTotalPages = Math.max(1, Math.ceil(availableFiltered.length / PAGE_SIZE));
  const availableCurrentPage = Math.min(availablePage, availableTotalPages);
  const availableRows = availableFiltered.slice(
    (availableCurrentPage - 1) * PAGE_SIZE,
    availableCurrentPage * PAGE_SIZE,
  );

  const importRepo = useMutation({
    mutationFn: (p: Project) =>
      api.importRepo({
        gitlabProjectId: p.id,
        pathWithNamespace: p.pathWithNamespace,
        name: p.name,
        defaultBranch: p.defaultBranch,
        webUrl: p.webUrl,
      }),
    onSuccess: async (repo) => {
      await qc.invalidateQueries({ queryKey: ['repos'] });
      navigate(`/repos/${repo.id}`);
    },
  });

  // Partagé entre la section "Dépôts favoris" et la liste principale (voir plus bas) — même carte,
  // seul l'ensemble parcouru diffère. « Retirer » désimporte le dépôt (≠ retirer des favoris) :
  // masqué dans l'onglet Favoris pour ne pas être confondu avec l'étoile.
  const renderImportedRow = (r: ImportedRepo, allowRemove: boolean) => (
    <div
      key={r.id}
      className="repo-card row"
      style={{ justifyContent: 'space-between' }}
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/repos/${r.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/repos/${r.id}`);
        }
      }}
    >
      <RepoTitle
        name={r.name}
        pathWithNamespace={r.pathWithNamespace}
        visibility={r.visibility}
        role={r.role}
      />
      <div
        className="row"
        style={{ gap: confirmingRemoveId === r.id ? 4 : 0 }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={`icon-button${favoriteIds.has(r.id) ? ' active' : ''}`}
          aria-label={favoriteIds.has(r.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          title={favoriteIds.has(r.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          onClick={() => toggleFavorite(r.id)}
          disabled={favoriteMutation.isPending}
        >
          <StarIcon filled={favoriteIds.has(r.id)} />
        </button>
        {allowRemove && r.role && REMOVABLE_ROLES.has(r.role) && (
          confirmingRemoveId === r.id ? (
            <>
              <button
                type="button"
                className="badge error"
                onClick={() => removeRepo.mutate(r.id)}
                disabled={removeRepo.isPending}
              >
                Confirmer ?
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => setConfirmingRemoveId(null)}
                disabled={removeRepo.isPending}
              >
                Annuler
              </button>
            </>
          ) : (
            <button
              type="button"
              className="icon-button"
              aria-label="Retirer de Clearflow"
              title="Retirer de Clearflow"
              onClick={() => setConfirmingRemoveId(r.id)}
            >
              <RemoveIcon />
            </button>
          )
        )}
      </div>
    </div>
  );

  return (
    <div className="repo-list-page">
      <div className="tabs">
        <button
          className={`tab-button${tab === 'favorites' ? ' active' : ''}`}
          onClick={() => setTab('favorites')}
        >
          <StarIcon filled /> Favoris
          {imported.data && <span className="tab-count">{favoritesFiltered.length}</span>}
        </button>
        <button
          className={`tab-button${tab === 'imported' ? ' active' : ''}`}
          onClick={() => setTab('imported')}
        >
          Dépôts importés
          {imported.data && <span className="tab-count">{importedFiltered.length}</span>}
        </button>
        <button
          className={`tab-button${tab === 'available' ? ' active' : ''}`}
          onClick={() => setTab('available')}
        >
          Disponibles sur GitLab
          {available.data && <span className="tab-count">{availableFiltered.length}</span>}
        </button>
      </div>

      <div className="commit-search-field" style={{ margin: '10px 0' }}>
        <svg className="commit-search-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5" />
          <line x1="10" y1="10" x2="14.5" y2="14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          className="commit-search-input"
          type="text"
          placeholder="Rechercher un dépôt…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setFavoritesPage(1);
            setImportedPage(1);
            setAvailablePage(1);
          }}
          spellCheck={false}
          style={search ? { paddingRight: 28 } : undefined}
        />
        {search && (
          <button
            type="button"
            className="commit-search-clear"
            aria-label="Effacer la recherche"
            onClick={() => {
              setSearch('');
              setFavoritesPage(1);
              setImportedPage(1);
              setAvailablePage(1);
            }}
          >
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="12" height="12">
              <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      {tab === 'favorites' && (
        <section className="col" style={{ gap: 8 }}>
          {imported.isLoading && <p className="muted">Chargement…</p>}
          {(imported.data?.length ?? 0) > 0 && favoritesFiltered.length === 0 && (
            <p className="muted">
              {search
                ? `Aucun favori ne correspond à « ${search} ».`
                : 'Aucun dépôt favori pour l\'instant. Clique sur l\'étoile d\'un dépôt dans l\'onglet « Dépôts importés » pour l\'ajouter ici.'}
            </p>
          )}
          {favoritesRows.map((r) => renderImportedRow(r, false))}
          <Pagination page={favoritesCurrentPage} totalPages={favoritesTotalPages} onChange={setFavoritesPage} />
        </section>
      )}

      {tab === 'imported' && (
        <section className="col" style={{ gap: 8 }}>
          {imported.isLoading && <p className="muted">Chargement…</p>}
          {imported.data?.length === 0 && (
            <p className="muted">
              Aucun dépôt importé pour l'instant. Choisis-en un dans l'onglet « Disponibles sur GitLab ».
            </p>
          )}
          {(imported.data?.length ?? 0) > 0 && importedFiltered.length === 0 && (
            <p className="muted">Aucun dépôt importé ne correspond à « {search} ».</p>
          )}
          {importedRows.map((r) => renderImportedRow(r, true))}
          <Pagination page={importedCurrentPage} totalPages={importedTotalPages} onChange={setImportedPage} />
        </section>
      )}

      {tab === 'available' && (
        <section className="col" style={{ gap: 8 }}>
          {available.isLoading && (
            <div className="row">
              <span className="spinning" aria-hidden="true">↻</span>
              <p className="muted">Chargement de la liste GitLab…</p>
            </div>
          )}
          {/* Les résultats arrivent page par page (streaming) — ce message reste affiché
              tant que le scan GitLab tourne encore, même une fois les premiers projets visibles. */}
          {!available.isLoading && available.isFetching && (
            <p className="muted" style={{ fontSize: 12 }}>
              Chargement en cours… {available.data?.length ?? 0} projets pour l'instant.
            </p>
          )}
          {available.isError && (
            <span className="badge error">Erreur : {(available.error as Error).message}</span>
          )}
          {notImported.length === 0 && !available.isLoading && !available.isError && (
            <p className="muted">Tous les dépôts accessibles sont déjà importés.</p>
          )}
          {notImported.length > 0 && availableFiltered.length === 0 && !available.isLoading && !available.isError && (
            <p className="muted">Aucun dépôt disponible ne correspond à « {search} ».</p>
          )}
          {availableRows.map((p) => (
            <div key={p.id} className="repo-card row" style={{ justifyContent: 'space-between' }}>
              <RepoTitle
                name={p.name}
                pathWithNamespace={p.pathWithNamespace}
                visibility={p.visibility}
                role={p.role}
              />
              <button
                type="button"
                className="icon-button"
                aria-label="Importer dans Clearflow"
                title="Importer dans Clearflow"
                onClick={() => importRepo.mutate(p)}
                disabled={importRepo.isPending}
              >
                <AddIcon />
              </button>
            </div>
          ))}
          <Pagination page={availableCurrentPage} totalPages={availableTotalPages} onChange={setAvailablePage} />
        </section>
      )}
    </div>
  );
}
